import { InputBaseComponentProps, styled } from '@mui/material';
import Icons from '@shared/components/core/images/Icons';
import { ErrorText } from '@shared/components/core/text/FormsTypography';
import { color } from '@shared/constants/Styles';
import { ChangeEventHandler, ComponentProps, FocusEventHandler, ReactNode, forwardRef } from 'react';
import StyledTextField from './StyledTextField';

interface IErrorHelpTextProps {
    helpMessage: string;
}

interface ITextFieldWithValidationProps extends ComponentProps<typeof StyledTextField> {
    label: string;
    value: string;
    placeholder: string;
    hasError: boolean;
    /** error message when a validation fails and an error is shown*/
    helperTextMessageOnError: string | null;
    /** Must contain the specific validation logic */
    onChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    inputProps?: InputBaseComponentProps;
    /** node to show for helperText when there are no errors, default is null */
    helperTextSuccess?: ReactNode;
    onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

const TextFieldWithValidation = forwardRef(function TextFieldWithValidation(
    {
        label,
        value,
        placeholder,
        hasError,
        helperTextMessageOnError,
        onChange,
        inputProps,
        helperTextSuccess = null,
        onFocus = onChange,
    }: ITextFieldWithValidationProps,
    ref
) {
    return (
        <StyledTextField
            inputRef={ref}
            slotProps={{ htmlInput: inputProps, formHelperText: { component: 'div' } as any }}
            fullWidth
            label={label}
            value={value}
            onChange={onChange}
            onFocus={onFocus}
            placeholder={placeholder}
            type="text"
            error={hasError}
            helperText={
                hasError && helperTextMessageOnError ? (
                    <ErrorHelpText helpMessage={helperTextMessageOnError} />
                ) : (
                    helperTextSuccess
                )
            }
        />
    );
});

const ErrorHelpText = ({ helpMessage }: IErrorHelpTextProps) => {
    return (
        <StyledErrorHelpText>
            <Icons name="warning" color={color.error} />
            <ErrorText>{helpMessage}</ErrorText>
        </StyledErrorHelpText>
    );
};

const StyledErrorHelpText = styled('div')`
    display: flex;
    gap: 0.5rem;
    padding: 5px;
`;

export default TextFieldWithValidation;
